/**
 * Copyright (c) 2026 eBUCIS, LLC. All rights reserved.
 *
 * usePlanAdherence - fetches this week's study-plan adherence (actual vs recommended hours)
 * for the plan-deviation dashboard banner. Returns behind/on_track/ahead plus the raw hours.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import predictionService from '../services/predictionService'

export function usePlanAdherence({ autoFetch = true } = {}) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(autoFetch)
  const [error, setError] = useState(null)

  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  const fetchAdherence = useCallback(async () => {
    if (!isMountedRef.current) return
    const currentRequestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    try {
      const response = await predictionService.getPlanAdherence()
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setData(response?.data || response || null)
      }
    } catch (err) {
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setError(err)
        setData(null)
      }
    } finally {
      if (isMountedRef.current && currentRequestId === requestIdRef.current) {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    if (autoFetch) fetchAdherence()
    return () => {
      isMountedRef.current = false
    }
  }, [autoFetch, fetchAdherence])

  return { data, loading, error, refresh: fetchAdherence }
}

export default usePlanAdherence
